import React, { useEffect, useState } from "react";

import { Button, Modal } from "@packages/ds";
import { useIntl } from "react-intl";

import Loading from "ds/PageLoading";
import useDecodedParams from "Hooks/useDecodedParams";
import { getOrganizationId } from "Libs/utils";
import { selectContinousProfillingSellableEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { useTrialInfo } from "Reducers/organization/hooks/useTrialnfo";
import { useLoadOrder } from "Reducers/organization/order/hooks/useLoadOrder";
import { useLoadOrders } from "Reducers/organization/order/hooks/useLoadOrders";
import { getOrganizationOrderAsOfNow } from "Reducers/organization/order/thunks/getOrganizationOrderAsOfNow.thunk";
import {
  selectSubscriptionDetails,
  loadSubscription as loadOrganizationSubscription
} from "Reducers/organization/subscription";
import { loadOneProject } from "Reducers/project/project";
import BillingWrapper from "src/organization/pages/billing/components/BillingWrapper/BillingWrapper";
import ProfilingSellableModalContent from "src/organization/pages/project/environment/metrics/profiling/sellable/Modal";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import AddonCta from "../components/Overview/AddonCta";
import Aside from "../components/Overview/Aside";
import * as O from "../components/Overview/BillingOverview.styles";
import * as S from "../organizationBilling/OrganizationBilling.style";

import ProjectBreakdown from "./components/ProjectBreakdown";

import type {
  FormattedCost,
  SubscriptionEstimateComplex
} from "@packages/client";

const ProjectBilling = () => {
  const intl = useIntl();

  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionEstimate, setSubscriptionEstimate] = useState<{
    current: SubscriptionEstimateComplex;
    future: SubscriptionEstimateComplex;
    continuousProfilingCost: FormattedCost;
  }>();
  const dispatch = useAppDispatch();
  const { organizationId, projectId, subscriptionId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    subscriptionId: string;
  }>();

  const subscription = useAppSelector(state =>
    selectSubscriptionDetails(state, {
      organizationId,
      subscriptionId
    })
  );

  const continuousProfilingEnabled = useAppSelector(
    selectContinousProfillingSellableEnabled
  );

  const orgId = useAppSelector(state =>
    getOrganizationId(state, organizationId)
  );

  const showContinuousProfilingAddon =
    continuousProfilingEnabled && subscription?.continuous_profiling === null;

  const [showContinuousProfilingModal, setShowContinuousProfilingModal] =
    useState(false);

  const { recurring, loading: loadingOrders } = useLoadOrders(organizationId);

  const recurringOrders = Object.values(recurring || {})[0];

  const { order, loading: loadingOrder } = useLoadOrder({
    organizationId,
    orderId: recurringOrders?.id,
    includeDetails: true
  });

  const asOfOrder = useAppSelector(
    state => state.organizationOrder.asOf[order?.id ?? ""]
  );

  useEffect(() => {
    projectId &&
      dispatch(
        loadOneProject({
          projectId,
          organizationId,
          loadActivitiesAndSubscribe: false
        })
      );
  }, [dispatch, projectId, organizationId]);

  useEffect(() => {
    if (subscriptionId && organizationId)
      dispatch(
        loadOrganizationSubscription({
          organizationId,
          id: subscriptionId
        })
      );
  }, [dispatch, subscriptionId, organizationId]);

  useEffect(() => {
    const getSubscriptionEstimate = async () => {
      if (!subscription?.getEstimate) return;
      try {
        setSubscriptionLoading(true);
        const current = await subscription.getEstimate({
          current_month: true,
          format: "complex",
          include_components: true
        });
        const future = await subscription.getEstimate({
          format: "complex",
          include_components: true
        });
        const continuousProfilingCost = (
          await subscription.getEstimate({
            continuous_profiling: "UPSUN-FEATURE-CONTINUOUS-PROFILING",
            format: "complex"
          })
        ).options.continuous_profiling!;
        setSubscriptionEstimate({ current, future, continuousProfilingCost });
      } finally {
        setSubscriptionLoading(false);
      }
    };
    getSubscriptionEstimate();
  }, [subscription]);

  useEffect(() => {
    if (orgId && order) {
      dispatch(
        getOrganizationOrderAsOfNow({
          organizationId: orgId,
          orderId: order.id
        })
      );
    }
  }, [dispatch, order, orgId, organizationId]);

  const { shouldShowFreeDuringTrialTag, isTrial } = useTrialInfo();

  if (subscriptionLoading || !subscription || loadingOrders || loadingOrder) {
    return <Loading />;
  }

  const projectCurrentSubtotal = asOfOrder?.line_items.find(
    item =>
      item.subscription_id === subscription.id &&
      item.product === "Project subtotal"
  )?.total_price_formatted;

  const Addons = () => (
    <O.Stack>
      <O.BillingTitle tag="h2">
        {intl.formatMessage({
          id: `project.billing.project_addons`
        })}
      </O.BillingTitle>

      <AddonCta
        title={intl.formatMessage({
          id: "project.billing.continuous_profiling.title"
        })}
        body={intl.formatMessage(
          {
            id: "project.billing.continuous_profiling.body"
          },
          {
            price: subscriptionEstimate?.continuousProfilingCost.formatted
          }
        )}
        tagVariant="rose"
        tag={
          shouldShowFreeDuringTrialTag
            ? intl.formatMessage({
                id: "free_during_trial"
              })
            : undefined
        }
        buttons={
          <Button
            onClick={() => {
              setShowContinuousProfilingModal(true);
            }}
          >
            {intl.formatMessage({
              id: "organization.billing.enable"
            })}
          </Button>
        }
      />
    </O.Stack>
  );

  return (
    <BillingWrapper>
      <S.PageWrapper>
        <Aside
          page="project"
          currentSpend={projectCurrentSubtotal}
          totalCurrentMonth={
            subscriptionEstimate?.current.components?.component_total.formatted
          }
          totalNextMonth={subscriptionEstimate?.future.total.formatted}
          isTrial={isTrial}
        >
          {showContinuousProfilingAddon && <Addons />}
        </Aside>
        <ProjectBreakdown
          subscription={subscription}
          estimateCurrent={subscriptionEstimate?.current}
          estimateFuture={subscriptionEstimate?.future}
          order={order}
          asOfOrder={asOfOrder}
          isTrial={isTrial}
          continuousProfilingCost={
            subscriptionEstimate?.continuousProfilingCost
          }
        />
        <Modal
          handleClose={() => {
            setShowContinuousProfilingModal(false);
          }}
          isOpen={showContinuousProfilingModal}
          hasOverlay={true}
        >
          <ProfilingSellableModalContent
            freeOnTrial={true}
            onClose={() => {
              setShowContinuousProfilingModal(false);
            }}
            subscription={subscription}
            sellableName="continuous_profiling"
          />
        </Modal>
      </S.PageWrapper>
    </BillingWrapper>
  );
};

export default ProjectBilling;
